import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  MouseSensor,
  useDndContext,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { memo, ReactNode, useCallback } from "react";
import { CellValueRendererFile } from "../cell-value-renderers/CellValueRendererFile";
import { CellAddress } from "../store/DataTableProvider.types";
import { useDataTableContext } from "../store/DataTableProvider";
import { CellValue, Group, IdeasFile } from "@inscopix/ideas-hyperformula";
import { convertCellValueToStaticFormula } from "../store/SideEffects";
import { isEqual } from "lodash";
import { uuid } from "utils/uuid";
import { client } from "providers/ApolloProvider/ApolloProvider";
import {
  CellValueRendererFileFieldsFragment,
  CellValueRendererFileFieldsFragmentDoc,
} from "graphql/_Types";
import { captureException } from "@sentry/react";
import { FileSource } from "types/constants";

/**
 * Previously we used data fed to the context from DraggableFileBadge via the useDraggable hook,
 * However, because DraggableFileBadge was rendered in a virtualized eui data grid (File Browser),
 * it could unmount once a drag commenced, resulting in the draggable data being lost while a drag event was still in flight.
 * This resulted in the drag overlay disappearing, and a drop event being captured with undefined data and a subsequent application crash.
 *
 * The interim solution is to encode our draggable data in an id, which is static and fixed throughout
 * the entire lifecycle of a drag event.
 *
 * Alternative solutions:
 * 1) Prevent the grid from scrolling to prevent unmounting the source draggable badge via preventing default
 * on mouseDown. While this worked, it feels dangerous and could have unintended side effects. Also since virtualization is not within our control
 * we can't necessarily guarantee the component will always stay mounted through the entire drag event.
 *
 * 2) Manually manage draggable data in state via events like dragStart and dragEnd. This felt a bit more complex and hard to follow while not really
 * providing a ton of benefit. This data would also need to be managed in a separate context because things like the recordings grid cell renderer
 * need access to the draggable data
 */

type DataTableDraggableData = { address?: CellAddress; fileId: string };

/**
 * encodes information in the draggable data id because data set by useDraggable
 * is lost mid drag if the source component unmounts
 * @param data Draggable data to turn into a parsable unique id
 * @returns
 */

export const generateDataTableDraggableId = (data: DataTableDraggableData) =>
  JSON.stringify({ id: uuid(), data });

/**
 * Parse information encoded in draggable ids as a way around a bug where draggable data set useDraggable
 * is lost mid drag if the source component unmounts
 * @param draggableId id from dnd active
 * @returns Draggable data
 */

export const parseDataTableDraggableId = (draggableId: string) =>
  (JSON.parse(draggableId) as { data: DataTableDraggableData }).data;

const DataTableDragOverlay = memo(function DataTableDragOverlay() {
  const { active } = useDndContext();

  if (active === null) {
    return null;
  }

  const activeData = parseDataTableDraggableId(active.id as string);

  return (
    <DragOverlay zIndex={9000} dropAnimation={null}>
      <CellValueRendererFile
        fileId={activeData.fileId}
        suppressContextMenu
        suppressTooltip
      />
    </DragOverlay>
  );
});

interface DataTableDragDropProviderProps {
  children: ReactNode;
}

export const DataTableDragDropProvider = ({
  children,
}: DataTableDragDropProviderProps) => {
  const unassignFile = useDataTableContext((s) => s.unassignFile);
  const setCellFormula = useDataTableContext((s) => s.setCellFormula);

  const handleDragEnd = useCallback(
    (e: DragEndEvent) => {
      const { active, over } = e;

      // Do nothing if drag does not end over a drop zone
      if (over === null) {
        return;
      }

      const activeData = parseDataTableDraggableId(active.id as string);
      const overData = over.data.current as
        | { address: CellAddress; value: CellValue }
        | undefined;

      // Do nothing if draggable or droppable data is missing
      if (activeData === undefined || overData === undefined) {
        return;
      }

      const cellAlreadyContainsFile =
        (overData.value instanceof IdeasFile &&
          overData.value.attrs.id === activeData.fileId) ||
        (overData.value instanceof Group &&
          overData.value.cellValues.some(
            (v) => v instanceof IdeasFile && v.attrs.id === activeData.fileId,
          ));

      // unassign the file from the current cell (even if it already exists in the target cell)
      // unless the file is being dropped on the same cell it is already assigned to
      if (
        activeData.address !== undefined &&
        !isEqual(activeData.address, overData.address)
      ) {
        void unassignFile({
          fileId: activeData.fileId,
          address: activeData.address,
        });
      }

      // Do not assign if cell already contains the file
      if (cellAlreadyContainsFile) {
        return;
      }

      const file =
        client.cache.readFragment<CellValueRendererFileFieldsFragment>({
          fragment: CellValueRendererFileFieldsFragmentDoc,
          id: `File:${activeData.fileId}`,
          returnPartialData: false,
        });

      if (file === null) {
        return captureException("Missing file cache when handling drop event", {
          extra: { fileId: activeData.fileId },
        });
      }

      const ideasFile = new IdeasFile({
        ...file,
        tags: [],
        source: file.source as FileSource,
      });

      // Calculate the new value of the target cell
      const newValue = (() => {
        if (overData.value instanceof IdeasFile) {
          return new Group([overData.value, ideasFile]);
        } else if (overData.value instanceof Group) {
          return new Group([...overData.value.cellValues, ideasFile]);
        } else {
          return ideasFile;
        }
      })();

      void setCellFormula({
        address: overData.address,
        formula: convertCellValueToStaticFormula(newValue),
      });
    },
    [setCellFormula, unassignFile],
  );

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      // Mouse must move at least 1 pixel before a drag start event is emitted
      // This allows click events to be detected
      distance: 1,
    },
  });
  const sensors = useSensors(mouseSensor);

  return (
    <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
      {children}
      <DataTableDragOverlay />
    </DndContext>
  );
};
